import { useQuery } from '@tanstack/react-query'
import {
	Button,
	Checkbox,
	CloseIcon,
	FiltersIcon,
	Modal,
	ModuleHeader,
	SpinnerWithBackground,
	Tags,
	Text,
} from '@viapeople/components'
import { useState } from 'react'
import { useFeatureFlag } from '../../hooks/useFeatureFlag'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'
import { REPORT_TYPES } from './shared/reportTypes'
import { getVisibleReports, isReportAvailable } from './shared/reportUtils'
import { useExcelExport } from './shared/useExcelExport'

const CUSTOM_FIELD_REPORT_TYPES = new Set(['overall-status', 'status-by-reviewer'])

export const DetailedStatusReports = () => {
	const { showToast } = useToast()
	const [showFilters, setShowFilters] = useState(false)
	const [selectedCycles, setSelectedCycles] = useState<string[]>([])
	const [selectedCustomFields, setSelectedCustomFields] = useState<Record<string, string[]>>({})

	const [tempSelectedCycles, setTempSelectedCycles] = useState<string[]>([])
	const [tempSelectedCustomFields, setTempSelectedCustomFields] = useState<Record<string, string[]>>({})

	const { data: cyclesData } = useQuery({
		...trpc.client.dashboard.getCyclesInfo.queryOptions(),
	})

	const { data: disabledStatusReportIds = [] } = useQuery({
		...trpc.client.reports.getDisabledStatusReportIds.queryOptions(),
	})

	const activeCycles = showFilters ? tempSelectedCycles : selectedCycles
	const { data: customFieldsData } = useQuery({
		...trpc.client.reports.getAvailableCustomFields.queryOptions({
			cycleIds: activeCycles,
		}),
		enabled: activeCycles.length > 0,
	})

	const { data: orgHasExtraCustomFields } = useQuery({
		...trpc.client.reports.hasOrgCustomFieldsForCycles.queryOptions({
			cycleIds: selectedCycles,
		}),
		enabled: selectedCycles.length > 0,
	})

	const cycles = cyclesData || []

	const handleTempCycleToggle = (cycleValue: string) => {
		setTempSelectedCycles((prev) => {
			const next = prev.includes(cycleValue) ? prev.filter((id) => id !== cycleValue) : [...prev, cycleValue]
			if (next.length === 0) {
				setTempSelectedCustomFields({})
			}
			return next
		})
	}

	const handleTempCustomFieldToggle = (fieldName: string, fieldValue: string) => {
		setTempSelectedCustomFields((prev) => {
			const currentValues = prev[fieldName] || []
			const newValues = currentValues.includes(fieldValue)
				? currentValues.filter((val) => val !== fieldValue)
				: [...currentValues, fieldValue]

			if (newValues.length === 0) {
				const { [fieldName]: _, ...rest } = prev
				return rest
			}

			return {
				...prev,
				[fieldName]: newValues,
			}
		})
	}

	const handleSaveFilters = () => {
		setSelectedCycles(tempSelectedCycles)
		setSelectedCustomFields(tempSelectedCustomFields)
		setShowFilters(false)
		showToast('Filters applied successfully')
	}

	const handleCancelFilters = () => {
		setTempSelectedCycles(selectedCycles)
		setTempSelectedCustomFields(selectedCustomFields)
		setShowFilters(false)
	}

	const handleOpenFilters = () => {
		setTempSelectedCycles(selectedCycles)
		setTempSelectedCustomFields(selectedCustomFields)
		setShowFilters(true)
	}

	const getSelectedTags = () => {
		const tags: { id: string; label: string }[] = []

		selectedCycles.forEach((cycleId) => {
			const cycle = cycles.find((c) => c.id === cycleId)
			if (cycle) {
				tags.push({
					id: `cycle-${cycle.id}`,
					label: `${cycle.cycleInfo.name || 'Cycle'} (${cycle.cycleInfo.status})`,
				})
			}
		})

		Object.entries(selectedCustomFields).forEach(([fieldName, values]) => {
			values.forEach((value) => {
				tags.push({
					id: `${fieldName}-${value}`,
					label: `${fieldName}: ${value}`,
				})
			})
		})

		return tags
	}

	const removeTag = (tagId: string) => {
		if (tagId.startsWith('cycle-')) {
			const cycleId = tagId.replace('cycle-', '')
			setSelectedCycles((prev) => {
				const next = prev.filter((id) => id !== cycleId)
				if (next.length === 0) {
					setSelectedCustomFields({})
				}
				return next
			})
		} else {
			const [fieldName, ...valueParts] = tagId.split('-')
			const value = valueParts.join('-')
			setSelectedCustomFields((prev) => {
				const currentValues = prev[fieldName] || []
				const newValues = currentValues.filter((val) => val !== value)
				if (newValues.length === 0) {
					const { [fieldName]: _, ...rest } = prev
					return rest
				}
				return { ...prev, [fieldName]: newValues }
			})
		}
	}

	const { exportReport, isLoading } = useExcelExport()
	const { isEnabled: bglPivotEnabled } = useFeatureFlag('bgl-pivot-report')

	const [exportModalReportId, setExportModalReportId] = useState<string | null>(null)
	const [includeCustomFields, setIncludeCustomFields] = useState(false)

	// Fail-OPEN intent: when the gate query is loading/errored/disabled,
	// `orgHasExtraCustomFields` is `undefined` and we default to `true` so the
	// "Include all custom fields" modal still surfaces. This is a deliberate
	// behaviour delta vs pre-fix (which suppressed the modal during loading).
	// DO NOT "simplify" this to `=== true` — that flips the load-state to
	// fail-CLOSED and re-introduces Wrike #4402063112 during the 50-200ms
	// query window.
	const hasExtraCustomFields = orgHasExtraCustomFields !== false

	const handleWebClick = () => {
		showToast('Web view coming soon', 'info')
	}

	const isMultiCycleSelected = selectedCycles.length > 1
	const hasCycles = selectedCycles.length > 0

	const buildFilters = () => Object.entries(selectedCustomFields).map(([field, values]) => ({ field, values }))

	const handleExcelClick = (reportId: string) => {
		if (selectedCycles.length === 0) {
			showToast('Please select at least one cycle from the filters', 'error')
			return
		}

		if (CUSTOM_FIELD_REPORT_TYPES.has(reportId) && hasExtraCustomFields) {
			setExportModalReportId(reportId)
			setIncludeCustomFields(false)
			return
		}

		exportReport({
			reportType: reportId,
			cycleIds: selectedCycles,
			filters: buildFilters(),
		})
	}

	const handleExportFromModal = () => {
		if (!exportModalReportId) return
		exportReport({
			reportType: exportModalReportId,
			cycleIds: selectedCycles,
			filters: buildFilters(),
			includeCustomFields,
		})
		setExportModalReportId(null)
		setIncludeCustomFields(false)
	}

	return (
		<div data-testid='detailed-status-page' className='p-6'>
			<div className='relative mb-6'>
				<ModuleHeader
					title='Detailed Status Reports'
					breadcrumb={[
						{ label: 'Dashboard', to: '/dashboard' },
						{ label: 'Reports & Files', to: '/reports' },
						{ label: 'Detailed Status Reports' },
					]}
					buttons={[
						{
							label: 'Filters',
							variant: 'secondary',
							icon: FiltersIcon,
							onClick: handleOpenFilters,
						},
					]}
				/>

				{showFilters && (
					<div className='absolute min-w-40 max-w-[80%] bottom-[-10px] right-0 translate-y-1/1 bg-neutral-0 rounded-lg border border-neutral-200 z-10'>
						<div className='flex items-center p-4 justify-between border-b border-neutral-200'>
							<Text variant='h6' weight='semibold'>
								Filters
							</Text>
							<button type='button' className='cursor-pointer' onClick={handleCancelFilters}>
								<CloseIcon />
							</button>
						</div>
						<div className='flex gap-4 p-4 flex-wrap max-h-96 overflow-y-auto'>
							{/* Always show cycles section */}
							<div className='flex flex-col min-w-48'>
								<Text variant='small' weight='semibold' className='mb-3'>
									Cycles
								</Text>
								<div className='flex flex-col gap-2'>
									{cycles.length > 0 ? (
										cycles.map((cycle) => (
											<Checkbox
												key={cycle.id}
												label={`${cycle.cycleInfo.name || 'Cycle'} (${cycle.cycleInfo.status})`}
												checked={tempSelectedCycles.includes(cycle.id)}
												onChange={() => handleTempCycleToggle(cycle.id)}
											/>
										))
									) : (
										<Text variant='small' className='text-neutral-500'>
											No cycles available
										</Text>
									)}
								</div>
							</div>

							{/* Show custom fields only when a cycle is selected */}
							{tempSelectedCycles.length > 0 &&
								customFieldsData &&
								customFieldsData.map((field) => (
									<div key={field.name} className='flex flex-col min-w-48'>
										<Text variant='small' weight='semibold' className='mb-3'>
											{field.name}
										</Text>
										<div className='flex flex-col gap-2'>
											{field.values.map((value) => (
												<Checkbox
													key={`${field.name}-${value}`}
													label={value}
													checked={tempSelectedCustomFields[field.name]?.includes(value) || false}
													onChange={() => handleTempCustomFieldToggle(field.name, value)}
												/>
											))}
										</div>
									</div>
								))}
						</div>
						<div className='flex items-center justify-end gap-3 p-4 border-t border-neutral-200'>
							<Button variant='secondary' size='small' onClick={handleCancelFilters}>
								Cancel
							</Button>
							<Button variant='primary' size='small' onClick={handleSaveFilters}>
								Apply Filters
							</Button>
						</div>
					</div>
				)}
			</div>

			<div className='flex gap-2 mb-6'>
				<Tags onRemove={removeTag} tags={getSelectedTags()} />
			</div>

			{selectedCycles.length === 0 ? (
				<div className='bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6'>
					<Text variant='medium' className='text-blue-800'>
						Click the <strong>Filters</strong> button above to select one or more cycles. Select multiple cycles to
						generate a consolidated Overall Status report.
					</Text>
				</div>
			) : (
				<Text variant='medium' className='text-neutral-600 mb-6'>
					{selectedCycles.length > 1
						? 'Multiple cycles selected. Only the Overall Status report supports multi-cycle export.'
						: 'Generate and download detailed reports for your selected cycle. Use custom field filters to further refine your data.'}
				</Text>
			)}

			{Object.entries(REPORT_TYPES).map(([sectionTitle, reports]) => {
				const visibleReports = getVisibleReports(reports, disabledStatusReportIds)
				if (visibleReports.length === 0) return null

				return (
					<div key={sectionTitle} className='mb-8'>
						<Text variant='h5' weight='semibold' className='mb-4'>
							{sectionTitle}
						</Text>
						<div className='space-y-4'>
							{visibleReports.map((report) => {
								const reportIsAvailable = isReportAvailable(report, customFieldsData)
								const isDisabled =
									!hasCycles || !reportIsAvailable || (isMultiCycleSelected && !report.supportsMultiCycle)

								return (
									<div
										key={report.id}
										className={`flex items-center justify-between p-4 border border-neutral-200 rounded-lg ${reportIsAvailable ? '' : 'bg-neutral-50'}`}>
										<div>
											<Text variant='medium' weight='semibold' className={reportIsAvailable ? '' : 'text-neutral-500'}>
												{report.title}
											</Text>
											{!reportIsAvailable && report.requiresCustomField && (
												<Text variant='xsmall' className='text-orange-600 mt-1'>
													Requires '{report.requiresCustomField}' custom field
												</Text>
											)}
											{isMultiCycleSelected && !report.supportsMultiCycle && hasCycles && (
												<Text variant='xsmall' className='text-neutral-500 mt-1'>
													Only available for single cycle selection
												</Text>
											)}
										</div>
										<div className='flex space-x-2'>
											<Button variant='secondary' size='small' onClick={handleWebClick} disabled={isDisabled}>
												Web
											</Button>
											<Button
												variant='primary'
												size='small'
												onClick={() => handleExcelClick(report.id)}
												disabled={isDisabled || isLoading}>
												{isLoading ? (
													<>
														<SpinnerWithBackground spinnerClass='w-4 h-4 mr-2' />
														Downloading...
													</>
												) : (
													'Excel'
												)}
											</Button>
										</div>
									</div>
								)
							})}
						</div>
					</div>
				)
			})}

			{bglPivotEnabled && (
				<div className='mb-8' data-testid='bgl-pivot-section'>
					<Text variant='h5' weight='semibold' className='mb-4'>
						BGL Leadership Reports
					</Text>
					<div className='space-y-4'>
						<div className='flex items-center justify-between p-4 border border-neutral-200 rounded-lg'>
							<div>
								<Text variant='medium' weight='semibold'>
									BGL Leadership Pivot Report
								</Text>
								{selectedCycles.length !== 1 && hasCycles && (
									<Text variant='xsmall' className='text-neutral-500 mt-1'>
										Select exactly one cycle to export
									</Text>
								)}
							</div>
							<Button
								variant='primary'
								size='small'
								onClick={() => handleExcelClick('bgl-pivot-report')}
								disabled={selectedCycles.length !== 1 || isLoading}>
								{isLoading ? (
									<>
										<SpinnerWithBackground spinnerClass='w-4 h-4 mr-2' />
										Downloading...
									</>
								) : (
									'Excel'
								)}
							</Button>
						</div>
					</div>
				</div>
			)}

			<Modal
				isOpen={exportModalReportId !== null}
				onClose={() => setExportModalReportId(null)}
				title='Export Report'
				buttons={[
					{ children: 'Cancel', variant: 'secondary', onClick: () => setExportModalReportId(null) },
					{ children: 'Export', variant: 'primary', onClick: handleExportFromModal },
				]}>
				<div className='flex flex-col gap-4'>
					<Text variant='medium'>Export will generate an Excel file for the selected cycle(s).</Text>
					<Checkbox
						label='Include all custom fields'
						checked={includeCustomFields}
						onChange={() => setIncludeCustomFields((prev) => !prev)}
						data-testid='include-custom-fields-checkbox'
					/>
				</div>
			</Modal>
		</div>
	)
}
